import * as XLSX from 'xlsx';

const dateLabel = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); // "Mon, May 18"

const isoKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const eachDay = (startStr, endStr) => {
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const cur = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const days = [];
  while (cur <= end) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const extractText = (text) => {
  if (!text) return '';

  // If it's already an object (ADF), extract text nodes recursively
  if (typeof text === 'object') {
    if (text.type === 'text') return text.text || '';
    if (Array.isArray(text.content)) return text.content.map(extractText).join(' ').trim();
    if (text.content) return extractText(text.content);
    return '';
  }

  if (typeof text === 'string') {
    // Strip any ADF-like JSON objects from the string
    // Handles both {"type"=>"doc"...} and {"type":"doc"...} variants
    return text
      .replace(/\{[^{}]*"type"\s*[=:>]+\s*"doc"[^{}]*\}/gs, '')
      .replace(/\{[^{}]*"type"\s*[=:>]+\s*"paragraph"[^{}]*\}/gs, '')
      .replace(/\{[^{}]*"version"\s*[=:>]+\s*\d[^{}]*\}/gs, '')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.startsWith('{'))
      .join(', ')
      .trim();
  }

  return String(text);
};

export const exportMemberTimesheet = ({ memberName, projects, startDate, endDate }) => {
  if (!projects || projects.length === 0) {
    return false;
  }

  const totalsCol = 1 + projects.length * 2; // 0-based index of the Totals column

  // Header: '' | <project> '' (merged) | ... | Totals
  const header = [''];
  projects.forEach((p) => header.push(p.project_key || p.project_name || '', ''));
  header.push('Totals');

  const aoa = [header];

  // One row per calendar day; empty days show 0.00 in Totals
  eachDay(startDate, endDate).forEach((d) => {
    const key = isoKey(d);
    const row = [dateLabel(d)];
    let dayTotal = 0;
    projects.forEach((p) => {
      const cell = p.days && p.days[key];
      if (cell) {
        row.push(extractText(cell.text), round2(cell.hours));
        dayTotal += Number(cell.hours) || 0;
      } else {
        row.push('', '');
      }
    });
    row.push(round2(dayTotal));
    aoa.push(row);
  });

  // Footer: Total hours per project + grand total
  const footer = ['Total hours'];
  let grand = 0;
  projects.forEach((p) => {
    footer.push('', round2(p.total_hours));
    grand += Number(p.total_hours) || 0;
  });
  footer.push(round2(grand));
  aoa.push(footer);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Merge each project key across its two header columns
  ws['!merges'] = projects.map((_, i) => {
    const first = 1 + i * 2;
    return { s: { r: 0, c: first }, e: { r: 0, c: first + 1 } };
  });

  // Column widths
  const cols = [{ wch: 14 }];
  projects.forEach(() => cols.push({ wch: 40 }, { wch: 9 }));
  cols.push({ wch: 10 });
  ws['!cols'] = cols;

  // 2-decimal format on every hours + totals cell
  const lastRow = aoa.length - 1;
  for (let r = 1; r <= lastRow; r += 1) {
    projects.forEach((_, i) => {
      const addr = XLSX.utils.encode_cell({ r, c: 2 + i * 2 });
      if (ws[addr] && typeof ws[addr].v === 'number') ws[addr].z = '0.00';
    });
    const totAddr = XLSX.utils.encode_cell({ r, c: totalsCol });
    if (ws[totAddr] && typeof ws[totAddr].v === 'number') ws[totAddr].z = '0.00';
  }

  const wb = XLSX.utils.book_new();
  const sheetName = String(memberName || 'Worklogs').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'Worklogs';
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const fileBase = (memberName || 'member').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  XLSX.writeFile(wb, `${fileBase}_${startDate}_${endDate}.xlsx`);
  return true;
};